<script setup lang="ts">
import { computed } from 'vue'

import { ButtonComponent, FlexComponent, Selector } from '../../'
import SortAscIcon from '../../../assets/icons/sort-asc.svg?component'
import SortDescIcon from '../../../assets/icons/sort-desc.svg?component'
import type { ListItems } from '../../../types/common.types'

const props = defineProps<{
  items: ListItems
  hasSeparateOrderSorting?: boolean
  shouldTruncateText?: boolean
}>()
const sortBy = defineModel<string>('modelValue', { default: 'created_at:desc' })
const sort = computed(() => (props.hasSeparateOrderSorting ? sortBy.value.split(':')[0] : sortBy.value))
const order = computed(() => (props.hasSeparateOrderSorting ? sortBy.value.split(':')[1] : undefined))
const sortLabel = computed(() => props.items.find((item) => item.value === sort.value)?.label)
const selectorOptions = computed(() => props.items.map((item, index) => ({ id: index, label: item.label })))
const selectedSelectorOption = computed(() => {
  const selectedIndex = props.items.findIndex((item) => item.value === sort.value)
  if (selectedIndex < 0) {
    return null
  }

  return {
    id: selectedIndex,
    label: props.items[selectedIndex]?.label,
  }
})

const changeSorting = (value: string) => (sortBy.value = `${value}${order.value ? `:${order.value}` : ''}`)
const changeOrder = () => (sortBy.value = `${sort.value}:${order.value === 'desc' ? 'asc' : 'desc'}`)
const handleSelectorChange = (option: { id: number; label?: string } | null) => {
  if (!option) {
    return
  }

  const selectedItem = props.items[option.id]
  if (!selectedItem) {
    return
  }

  changeSorting(selectedItem.value)
}
</script>
<template>
  <FlexComponent align="center">
    <ButtonComponent v-if="order" class="sorting-filter__order-button" variant="transparent" is-icon @click="changeOrder">
      <SortDescIcon v-if="order === 'desc'" />
      <SortAscIcon v-else />
    </ButtonComponent>
    <Selector
      class="sorting-filter__container"
      :options="selectorOptions"
      :selected-option="selectedSelectorOption"
      :selected-label="sortLabel ?? 'Sort by'"
      :should-truncate-text="shouldTruncateText"
      :show-selected-icon="false"
      :show-search="false"
      dropdown-label="Sort by"
      @update:selected-option="handleSelectorChange"
    />
  </FlexComponent>
</template>
<style lang="scss" scoped>
.sorting-filter__order-button {
  border-radius: 8px;
  &:hover {
    background: var(--button-hover);
  }
}
</style>
